/**
 * AreaManagement — TASK-023 / flow-22
 * Actor: biz_admin, sys_admin
 * Route: /admin/areas
 * API: GET /api/v1/area-codes, POST /api/v1/area-codes,
 *      GET /api/v1/village-chairmen, POST /api/v1/village-chairmen/:id/areas
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppModal,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type AreaCode = {
  code: string;
  name: string;
  ward: string;
  district: string;
  region: string;
  has_chairman: boolean;
};

type AssignedArea = { code: string; name: string };

type VillageChairman = {
  id: number;
  username: string;
  name: string;
  phone: string | null;
  assigned_areas: AssignedArea[];
};

function AssignAreasModal({
  open,
  chairman,
  areas,
  onClose,
  onSuccess,
}: {
  open: boolean;
  chairman: VillageChairman | null;
  areas: AreaCode[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setSelected([]);
      setErr("");
    }
  }, [open, chairman?.id]);

  const toggle = (code: string) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chairman) return;
    if (selected.length === 0) {
      setErr("Select at least one area code");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/village-chairmen/${chairman.id}/areas`, { area_codes: selected });
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Assignment failed");
    } finally {
      setBusy(false);
    }
  };

  if (!chairman) return null;

  const unassigned = areas.filter((a) => !a.has_chairman);
  const options = unassigned.length > 0 ? unassigned : areas;

  return (
    <AppModal open={open} onClose={onClose} title={`Assign Areas — ${chairman.name}`}>
      <form onSubmit={submit}>
        <p className="pg-text-muted" style={{ marginBottom: 12 }}>
          Select area codes to assign. Existing assignments to other chairmen will be revoked automatically.
        </p>
        <div className="pg-checkbox-list">
          {options.map((a) => (
            <label key={a.code} className="pg-checkbox-row">
              <input
                type="checkbox"
                checked={selected.includes(a.code)}
                onChange={() => toggle(a.code)}
                disabled={busy}
              />
              <span><strong>{a.code}</strong> — {a.name || a.ward} ({a.region})</span>
            </label>
          ))}
        </div>
        {options.length === 0 && <p>No area codes available.</p>}
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy || options.length === 0}>
            {busy ? <><IconSpinner size={14}/> Assigning…</> : "Assign Areas"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

function CreateAreaModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({ code: "", name: "", ward: "", district: "", region: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ code: "", name: "", ward: "", district: "", region: "" });
      setErr("");
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.ward.trim()) {
      setErr("Area code and ward are required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/area-codes", {
        code: form.code.trim().toUpperCase(),
        ward: form.ward.trim(),
        district: form.district.trim() || undefined,
        region: form.region.trim() || undefined,
        name: form.name.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create area code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Create Area Code">
      <form onSubmit={submit}>
        <p className="pg-text-muted" style={{ marginTop: 0 }}>
          Areas group customers by ward for reporting and village-chairman assignment. Codes must be unique.
        </p>
        <div className="pg-two-col-form">
          <AppField label="Area Code">
            <AppInput id="area-code" type="text" placeholder="e.g. WD-014" value={form.code} onChange={(e) => set("code", e.target.value)} disabled={busy} autoFocus />
          </AppField>
          <AppField label="Display Name (optional)">
            <AppInput id="area-name" type="text" placeholder="Defaults to ward" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Ward">
          <AppInput id="area-ward" type="text" value={form.ward} onChange={(e) => set("ward", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="District (optional)">
            <AppInput id="area-district" type="text" value={form.district} onChange={(e) => set("district", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Region (optional)">
            <AppInput id="area-region" type="text" value={form.region} onChange={(e) => set("region", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>
            {busy ? <><IconSpinner size={14}/> Creating…</> : "Create Area"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function AreaManagement() {
  const [areas, setAreas] = useState<AreaCode[]>([]);
  const [chairmen, setChairmen] = useState<VillageChairman[]>([]);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [activeTab, setActiveTab] = useState<"areas"|"chairmen">("areas");
  const [assignChairman, setAssignChairman] = useState<VillageChairman | null>(null);
  const [createAreaOpen, setCreateAreaOpen] = useState(false);

  const load = async () => {
    setPageState("loading");
    try {
      const [ad, cd] = await Promise.all([
        api.get<{ area_codes: AreaCode[] }>("/area-codes"),
        api.get<{ village_chairmen: VillageChairman[] }>("/village-chairmen"),
      ]);
      setAreas(ad.area_codes ?? []);
      setChairmen(cd.village_chairmen ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load areas");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, []);

  const areaColumns: ColumnDef<AreaCode, unknown>[] = [
    { accessorKey: "code", header: "Code" },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "ward", header: "Ward" },
    { accessorKey: "district", header: "District" },
    { accessorKey: "region", header: "Region" },
    {
      id: "chairman",
      header: "Village Chairman",
      cell: ({ row }) =>
        row.original.has_chairman
          ? <AppBadge tone="success"><IconCheck size={12}/> Assigned</AppBadge>
          : <AppBadge tone="warning">Unassigned</AppBadge>,
    },
  ];

  const chairmanColumns: ColumnDef<VillageChairman, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "username", header: "Username" },
    { accessorKey: "phone", header: "Phone" },
    {
      id: "areas",
      header: "Assigned Areas",
      cell: ({ row }) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {row.original.assigned_areas.length === 0
            ? <AppBadge tone="neutral">None</AppBadge>
            : row.original.assigned_areas.map((a) => (
              <AppBadge key={a.code} tone="info">{a.code}</AppBadge>
            ))
          }
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AppButton
          type="button"
          variant="secondary"
          onClick={(e) => { e.stopPropagation(); setAssignChairman(row.original); }}
        >
          Assign Areas
        </AppButton>
      ),
    },
  ];

  return (
    <>
      <AssignAreasModal
        open={!!assignChairman}
        chairman={assignChairman}
        areas={areas}
        onClose={() => setAssignChairman(null)}
        onSuccess={load}
      />
      <CreateAreaModal
        open={createAreaOpen}
        onClose={() => setCreateAreaOpen(false)}
        onSuccess={load}
      />

      <PageHeader
        title="Areas & Village Chairmen"
        subtitle="Area codes and chairman assignments."
        actions={
          activeTab === "areas" ? (
            <AppButton type="button" onClick={() => setCreateAreaOpen(true)}>Create Area</AppButton>
          ) : undefined
        }
      />

      <PageState status={pageState} error={pageError} onRetry={load}>
        <div className="pg-tabs">
          <button type="button" className={`pg-tab${activeTab === "areas" ? " active" : ""}`} onClick={() => setActiveTab("areas")}>Area Codes ({areas.length})</button>
          <button type="button" className={`pg-tab${activeTab === "chairmen" ? " active" : ""}`} onClick={() => setActiveTab("chairmen")}>Village Chairmen ({chairmen.length})</button>
        </div>
        {activeTab === "areas" && <AppCard><AppDataTable data={areas} columns={areaColumns} /></AppCard>}
        {activeTab === "chairmen" && <AppCard><AppDataTable data={chairmen} columns={chairmanColumns} /></AppCard>}
      </PageState>
    </>
  );
}
